import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountInsuranceApi, type AccountInsuranceListParams } from '../api/accountInsurance';
import { ApiError } from '../api/client';
import type {
  AccountInsurance, AccountInsuranceInput,
  MoveFootprint, MoveRequest, MoveResponse,
  Page, UUID, WriteResult,
} from '../api/types';

export const aiKeys = {
  all: ['account-insurance'] as const,
  list:   (p: AccountInsuranceListParams) => ['account-insurance', 'list', p] as const,
  // Task #245 (2026-06-02): includeDeleted in cache key.
  detail: (id: UUID, includeDeleted = false) =>
    ['account-insurance', 'detail', id, includeDeleted ? 'with-deleted' : 'alive'] as const,
};
export function useAccountInsuranceList(
  params: AccountInsuranceListParams,
  opts?: { enabled?: boolean },
) {
  // #39 (2026-06-11): zero pills → no fetch; page renders the hint.
  // #46 (2026-06-12): keepPreviousData — no loading flash on filter changes.
  return useQuery<Page<AccountInsurance>, ApiError>({
    queryKey: aiKeys.list(params),
    queryFn: () => accountInsuranceApi.list(params),
    enabled: opts?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}
export function useAccountInsurance(id: UUID | undefined, includeDeleted = false) {
  return useQuery<AccountInsurance, ApiError>({ queryKey: aiKeys.detail(id ?? '', includeDeleted), queryFn: () => accountInsuranceApi.get(id!, includeDeleted), enabled: !!id });
}
export function useCreateAccountInsurance() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, AccountInsuranceInput>({
    mutationFn: accountInsuranceApi.create,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: aiKeys.all }); if (!r.pending) qc.setQueryData(aiKeys.detail(r.entity.id), r.entity); },
  });
}
export function useUpdateAccountInsurance(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, AccountInsuranceInput>({
    mutationFn: (p) => accountInsuranceApi.update(id, p),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: aiKeys.all }); if (!r.pending) qc.setQueryData(aiKeys.detail(id), r.entity); },
  });
}
export function useDeleteAccountInsurance() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, UUID>({
    mutationFn: accountInsuranceApi.delete, onSuccess: () => qc.invalidateQueries({ queryKey: aiKeys.all }),
  });
}
export function useRestoreAccountInsurance() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, UUID>({
    mutationFn: accountInsuranceApi.restore, onSuccess: () => qc.invalidateQueries({ queryKey: aiKeys.all }),
  });
}


// Beta #11 cascade extension (2026-05-26).
import type { ReferencesReport } from '../api/persons';

export function useAccountInsuranceReferences(id: UUID | undefined, enabled = true) {
  return useQuery<ReferencesReport, ApiError>({
    queryKey: ['account-insurance', 'references', id ?? ''],
    queryFn: () => accountInsuranceApi.references(id!),
    enabled: enabled && !!id,
  });
}

interface CascadeDeleteInsuranceVars {
  id: UUID;
  refIds: Record<string, UUID[]>;
}

export function useCascadeDeleteAccountInsurance() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, CascadeDeleteInsuranceVars>({
    mutationFn: ({ id, refIds }) => accountInsuranceApi.cascadeDelete(id, refIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      qc.invalidateQueries({ queryKey: ['insurance-policy-parties'] });
      // ['insurance-policy-accounts'] dropped in #388 cleanup (2026-06-09):
      // the legacy query key is no longer used by any hook; the Accounts
      // join is now read via AccountInsurance.account_ids and invalidated
      // via aiKeys.all + ['accounts'].
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}

// ── #272 / #274 (2026-06-08) detach + attach Policy lifecycle moves ────

/** Detach an Active Policy. Server-side cascade to Indirect Accounts. */
export function useDetachPolicy() {
  const qc = useQueryClient();
  // #26 (2026-06-10): WriteResult-shaped — RM filings surface as .pending.
  return useMutation<WriteResult<AccountInsurance>, ApiError, UUID>({
    mutationFn: (id) => accountInsuranceApi.detach(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      // Indirect Account statuses changed → bust account queries too.
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
      // Policy→agreement detach cascades a billing-clear to every indirect
      // account via that policy (apply-path, C-214).
      qc.invalidateQueries({ queryKey: ['account-billing'] });
    },
  });
}

/** Attach a limbo (Inactive + agreement_id NULL) Policy to a target Agreement.
 *  2026-06-10: now WriteResult-shaped — the endpoint went maker-checker, so
 *  RM filings surface as .pending instead of a 403. */
export function useAttachPolicy() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, { id: UUID; agreementId: UUID }>({
    mutationFn: ({ id, agreementId }) => accountInsuranceApi.attach(id, agreementId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
      qc.invalidateQueries({ queryKey: ['account-billing'] });  // attaching a policy makes its indirect accounts related
    },
  });
}

/** #376 → #388 (2026-06-08 → renamed 2026-06-09): attach an orphan Account to
 *  an existing Active Policy as Indirect. Returns WriteResult shaped so the
 *  caller can inspect .pending / .changeRequestId from the X-Change-Request-*
 *  headers. Invalidates both account-insurance and accounts caches because
 *  the orphan transitions to Active with opened_by_policy_id set.
 *
 *  Replaces the deleted useCreateInsurancePolicyAccount hook. */
export function useAttachAccountToPolicy() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AccountInsurance>, ApiError, { policy_id: UUID; account_id: UUID }>({
    mutationFn: ({ policy_id, account_id }) => accountInsuranceApi.attachAccount(policy_id, account_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// ── #322 (2026-06-08) Terminate Policy (Redeem / Lapse / Mature) ────────
//
// Terminal Active → Closed transitions. Cascade Active Indirect Accounts
// to Closed. Server stamps close_reason atomically. Cannot be reversed.

/** Redeem an Active Policy — policyholder surrendered for cash value. */
export function useRedeemPolicy() {
  const qc = useQueryClient();
  // #26 (2026-06-10): WriteResult-shaped — RM filings surface as .pending.
  return useMutation<WriteResult<AccountInsurance>, ApiError, UUID>({
    mutationFn: (id) => accountInsuranceApi.redeem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}

/** Lapse an Active Policy — terminated due to non-payment of premiums. */
export function useLapsePolicy() {
  const qc = useQueryClient();
  // #26 (2026-06-10): WriteResult-shaped — RM filings surface as .pending.
  return useMutation<WriteResult<AccountInsurance>, ApiError, UUID>({
    mutationFn: (id) => accountInsuranceApi.lapse(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}

/** Mature an Active Policy — reached end of term and paid out. */
export function useMaturePolicy() {
  const qc = useQueryClient();
  // #26 (2026-06-10): WriteResult-shaped — RM filings surface as .pending.
  return useMutation<WriteResult<AccountInsurance>, ApiError, UUID>({
    mutationFn: (id) => accountInsuranceApi.mature(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}

// ── #104 Wave B (2026-06-08) move-on-attach hooks ───────────────────────

/** Preview the move footprint. Enabled flag controls when to fetch — typical
 *  use: only when both policyId and targetAgreementId are set AND the user
 *  has opened the move confirmation. */
export function useMovePolicyPreview(
  policyId: UUID | undefined,
  targetAgreementId: UUID | undefined,
  enabled = true,
) {
  return useQuery<MoveFootprint, ApiError>({
    queryKey: ['account-insurance', 'move-preview', policyId ?? '', targetAgreementId ?? ''],
    queryFn: () => accountInsuranceApi.movePreview(policyId!, targetAgreementId!),
    enabled: enabled && !!policyId && !!targetAgreementId,
    // Preview is fresh-on-every-modal-open; don't cache across sessions.
    staleTime: 0,
    gcTime: 0,
  });
}

/** Apply a move via maker-checker. Compliance/Admin auto-apply (200);
 *  RM filings queue a pending CR (202). Caller distinguishes by reading
 *  the `change_request_status` on the response body. */
export function useMovePolicy() {
  const qc = useQueryClient();
  return useMutation<
    WriteResult<MoveResponse>,
    ApiError,
    { id: UUID; body: MoveRequest }
  >({
    mutationFn: async ({ id, body }) => {
      const r = await accountInsuranceApi.move(id, body);
      return {
        entity: r.data,
        pending: r.headers.get('X-Change-Request-Status') === 'pending',
        changeRequestId: r.headers.get('X-Change-Request-Id'),
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      // The move can affect Indirect Accounts on the source side; bust accounts cache.
      qc.invalidateQueries({ queryKey: ['accounts'] });
      // And change_requests if a pending CR was filed.
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}
